// Shared bits for the Volt Box (admin studio) and the band's private preview page.
import axios from "axios";
import { API } from "../../App";

export const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }, withCredentials: true });
export const abs = (u) => (!u ? "" : u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`);
const pad = (n) => String(n).padStart(2, "0");

export const fmt = (s) => {
  if (s == null || Number.isNaN(s)) return "—";
  const t = Math.max(0, s), h = Math.floor(t / 3600), m = Math.floor((t % 3600) / 60), sec = Math.floor(t % 60);
  return h ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
};
export const fmtPrecise = (s) => `${s < 0 ? "−" : "+"}${Math.abs(s).toFixed(2)} s`;
export const SR = 48000; // every offset is counted in 48 kHz samples
export const fmtOffset = (samples, signed = true) => {
  const t = Math.abs(samples || 0) / SR, m = Math.floor(t / 60);
  return `${signed ? (samples < 0 ? "−" : "+") : ""}${m}:${(t - m * 60).toFixed(3).padStart(6, "0")}`;
};
export const parseTime = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const parts = s.split(":").map(Number);
  if (parts.some((n) => Number.isNaN(n))) return null;
  return parts.reduce((acc, n) => acc * 60 + n, 0);
};
export const fmtBytes = (b) => (!b ? "" : b > 1e9 ? `${(b / 1e9).toFixed(1)} GB` : b > 1e6 ? `${(b / 1e6).toFixed(0)} MB` : `${Math.round(b / 1e3)} KB`);
export const errMsg = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg.replace(/^Value error, /, "");
  return e?.message && !e.response ? e.message : fallback;
};

export const SOURCES = {
  studio: { label: "Studio", color: "#FF6B6B" },
  episode: { label: "Radio", color: "#54E3FF" },
  upload: { label: "Upload", color: "#F2CC8F" },
  link: { label: "Link", color: "#7FE3D6" },
  proton: { label: "Proton Drive", color: "#B39DFF" },
  mix: { label: "Blend", color: "#81C784" },
  desk: { label: "Mix desk", color: "#7FE3D6" },
  master: { label: "Release master", color: "#F2CC8F" },
  clean: { label: "Clean-up", color: "#7FE3D6" },
  video: { label: "From a video", color: "#FFB4A2" },
  band: { label: "Band's release", color: "#A5D6A7" },
  recorder: { label: "Recorder", color: "#FF6B6B" },
};

export const STRENGTHS = [
  { id: "light", label: "Light", hint: "Takes the edge off — keeps the room" },
  { id: "medium", label: "Medium", hint: "Cleaner low end, less hiss and harshness" },
  { id: "strong", label: "Strong", hint: "Most noise gone — tightest sound" },
];

// Same knobs as the server's clean-up chain (voltbox_audio.tone_chain), heard live in the browser.
export const TONE_KNOBS = [
  { key: "lowcut", label: "Low cut", hint: "Rumble and handling noise", min: 0, max: 200, step: 5, unit: "Hz" },
  { key: "body", label: "Body", hint: "Warmth of kick and bass · 110 Hz", min: -12, max: 9, step: 0.5, unit: "dB" },
  { key: "mud", label: "Mud", hint: "Boxy, cloudy middle · 320 Hz", min: -12, max: 9, step: 0.5, unit: "dB" },
  { key: "harsh", label: "Harshness", hint: "Bite of cymbals and voice · 3.2 kHz", min: -12, max: 9, step: 0.5, unit: "dB" },
  { key: "air", label: "Air", hint: "Sparkle on top · 9 kHz and up", min: -12, max: 9, step: 0.5, unit: "dB" },
  { key: "punch", label: "Punch", hint: "Tighter, more even hits", min: 0, max: 3, step: 1, unit: "" },
];
export const FLAT_TONE = { lowcut: 0, body: 0, mud: 0, harsh: 0, air: 0, punch: 0 };

const VCHUNK = 8 * 1024 * 1024;
export async function uploadVideo(itemId, file, { onProgress } = {}) {
  const total = Math.ceil(file.size / VCHUNK);
  if (total > 100) throw new Error("Videos up to 800 MB — export a smaller copy if it's bigger");
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  let res = null;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("chunk_index", i); fd.append("total_chunks", total); fd.append("filename", file.name);
    fd.append("file", file.slice(i * VCHUNK, (i + 1) * VCHUNK), `part${i}`);
    for (let a = 0; ; a += 1) {
      try { res = (await axios.post(`${API}/voltbox/${itemId}/video-chunk`, fd, auth())).data; break; }
      catch (e) { if (a >= 2 || e.response?.status === 400) throw e; await new Promise((r) => setTimeout(r, 1500 * (a + 1))); }
    }
    onProgress?.((i + 1) / total);
  }
  return res;
}

export const BAND = {
  approve: { label: "Band approved", cls: "bg-[#81C784]/15 text-[#A5D6A7] ring-[#81C784]/40" },
  changes: { label: "Band asked for changes", cls: "bg-[#F2CC8F]/15 text-[#F2CC8F] ring-[#F2CC8F]/40" },
};

export const dispatchPauseMusic = () => window.dispatchEvent(new CustomEvent("wall:pause-music"));
export const sameOrigin = (u) => { try { return new URL(abs(u), window.location.href).origin === window.location.origin; } catch { return false; } };

// Band Studio Phase 2 — what a take is, who made it, and the locked recipe behind it.
export const KINDS = {
  original: { label: "Original", color: "#F2CC8F", hint: "The file as it was uploaded — never changed" },
  rendered: { label: "From a recipe", color: "#7FE3D6", hint: "Rendered from a locked recipe — open it to see how it was made" },
  finished: { label: "Finished mix", color: "#A5D6A7", hint: "A finished file someone uploaded" },
};
export const ROLES = { admin: "THE WALL", band: "Band", fan: "Fan" };
export const RECIPE_KINDS = { layer: "Layer", desk: "Mix desk", shape: "Shape", songs: "Songs", clean: "Clean-up", band: "Band studio" };
export const fmtWhen = (iso) => (!iso ? "" : new Date(iso).toLocaleString([], { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }));
export const whoMade = (a) => (!a ? "THE WALL" : a.role === "admin" ? `THE WALL${a.name && a.name !== "THE WALL" ? ` · ${a.name}` : ""}` : a.name || ROLES[a.role] || "Someone");
export const saveRecipe = async (itemId, payload) => (await axios.post(`${API}/voltbox/${itemId}/recipes`, payload, auth())).data;
// "Open its recipe" on a take → the tool that made it loads the recipe (editing then saves a new one, based on it).
export const openRecipe = (recipe) => window.dispatchEvent(new CustomEvent("vb:open-recipe", { detail: recipe }));
export const saveFile = (name, text) => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([text], { type: "application/json" })); a.download = name; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 4000); };

const CHUNK = 4 * 1024 * 1024;
export async function uploadTake(itemId, file, { label = "", versionId = "", finished = false, note = "", tellBand = false, endpoint = null, onProgress } = {}) {
  const total = Math.ceil(file.size / CHUNK);
  if (total > 100) throw new Error("Files up to 400 MB — export a FLAC or 320k MP3 if it's bigger");
  const uploadId = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  let res = null;
  for (let i = 0; i < total; i += 1) {
    const fd = new FormData();
    fd.append("upload_id", uploadId); fd.append("chunk_index", i); fd.append("total_chunks", total);
    fd.append("filename", file.name); fd.append("label", label); fd.append("version_id", versionId);
    fd.append("finished", finished ? "1" : ""); fd.append("note", note); fd.append("tell_band", tellBand ? "1" : "");
    fd.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `part${i}`);
    for (let a = 0; ; a += 1) {
      try { res = (await axios.post(endpoint || `${API}/voltbox/${itemId}/audio-chunk`, fd, auth())).data; break; }
      catch (e) { if (a >= 2 || e.response?.status === 400) throw e; await new Promise((r) => setTimeout(r, 1500 * (a + 1))); }
    }
    onProgress?.((i + 1) / total);
  }
  return res;
}

export const randomCode = (n = 12) => Array.from(crypto.getRandomValues(new Uint8Array(n)), (b) => b % 10).join("").replace(/^0/, "7");
export const groupCode = (c) => (c || "").replace(/(\d{4})(?=\d)/g, "$1 ");
